import { gql, useQuery } from '@apollo/client';
import IconError from '@apollo/icons/default/IconError.svg';
import IconSuccess from '@apollo/icons/default/IconSuccess.svg';
import {
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Link,
  Code as OrbitCode,
  Select,
  Spinner,
  StatusBadge,
} from '@apollo/orbit';
import * as Sentry from '@sentry/react';
import { compare } from 'compare-versions';
import React, { useState } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';

import { launchesRouteConfig } from 'src/app/graph/routes';
import { Code } from 'src/components/code/Code';
import { HeaderTab } from 'src/components/common/headerTab/HeaderTab';
import { useGetSortedFederationVersions } from 'src/hooks/useGetSortedFederationVersions';
import { useSetDefaultBuildPipelineTrack } from 'src/hooks/useSetDefaultBuildPipelineTrack';
import { ampli } from 'src/lib/analytics/amplitude/vendor';
import { useIsFedNextEnabled } from 'src/lib/featureFlags';
import {
  BuildPipelineTrack,
  PublishUsingFederationQuery,
  PublishUsingFederationQueryVariables,
} from 'src/lib/graphqlTypes/types';
import { DocsRouter } from 'src/lib/routers';
import {
  introspectionToSubgraphPublish,
  schemaFileToSubgraphPublish,
} from 'src/lib/roverCommands';
export type PublishMode = 'schemaDocument' | 'introspection';

const publishUsingFederationQuery = gql<
  PublishUsingFederationQuery,
  PublishUsingFederationQueryVariables
>`
  query PublishUsingFederationQuery($graphId: ID!) {
    graph(id: $graphId) {
      id
      defaultBuildPipelineTrack
    }
  }
`;

export const FederationGettingStartedInstructions = ({
  firstPublish,
}: {
  firstPublish: boolean;
}) => (
  <>
    <p>
      <b>Need help getting started? </b>
      Check out our Federation{' '}
      <Link
        isExternal
        target="_blank"
        rel="noopener noreferrer"
        to={DocsRouter.path('FederationQuickstart')}
      >
        quick start guide
      </Link>{' '}
      in the Apollo docs where you can learn more on getting started with
      Federation and Rover, our command-line tool for interacting with the
      schema registry.{' '}
      {firstPublish ? (
        <>
          Once you're ready, follow the instructions below to start your first
          publish.
        </>
      ) : (
        <>
          Set up your CI/CD pipeline by following the instructions below to
          automatically keep your schema current in Studio on each code commit.
        </>
      )}
    </p>
  </>
);

export const PublishUsingFederation = ({
  firstPublish,
  graphId,
  graphVariant,
  serviceApiToken,
  isPublished,
  hasBuildErrors,
  onClose,
}: {
  firstPublish: boolean;
  graphId: string;
  graphVariant: string;
  serviceApiToken: string;
  isPublished: boolean;
  hasBuildErrors: boolean;
  onClose?: () => void;
}) => {
  const isFedNextEnabled = useIsFedNextEnabled();
  const [currentTab, setCurrentTab] = useState<PublishMode>('schemaDocument');
  const { data, loading } = useQuery(publishUsingFederationQuery, {
    variables: { graphId },
  });

  const [updateBuildPipeline, { loading: updateLoading, error: updateError }] =
    useSetDefaultBuildPipelineTrack({
      update(cache, { data: mData }) {
        if (mData?.graph?.setDefaultBuildPipelineTrack) {
          cache.modify({
            id: cache.identify({ __typename: 'Service', id: graphId }),
            fields: {
              defaultBuildPipelineTrack: () =>
                mData?.graph?.setDefaultBuildPipelineTrack,
            },
          });
        }
      },
    });

  const fileSchemaScript = schemaFileToSubgraphPublish({
    apiKey: serviceApiToken,
    graphId,
    graphVariant,
    schemaLocation: './products-schema.graphql',
    routingUrl: 'http://products.prod.svc.cluster.local:4001/graphql',
    highlightGraphRef: false,
  });

  const introspectSchemaScript = introspectionToSubgraphPublish({
    apiKey: serviceApiToken,
    graphId,
    graphVariant,
    routingUrl: 'http://products.svc.cluster.local:4001/graphql',
    highlightGraphRef: false,
  });

  const handleBuildPipelineChange = async (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    try {
      await updateBuildPipeline({
        variables: {
          graphId,
          version: e.target.value,
        },
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  if (firstPublish && isPublished) {
    ampli.schemaPublished({
      'API Type': 'GraphQL',
      OnboardingVariant: 'GraphOS-Trial',
    });
  }
  const fedVersionOptions = useGetSortedFederationVersions({
    includeFedNext: isFedNextEnabled,
    includeFed1: false,
  });

  return (
    <>
      <div
        data-testid="publish-schema-instructions"
        className="-mt-2 mb-4 border-b border-primary"
      >
        <HeaderTab
          tabName="Schema document"
          tabID="schemaDocument"
          isActive={() => currentTab === 'schemaDocument'}
          onClick={() => setCurrentTab('schemaDocument')}
        />
        <HeaderTab
          tabName="Introspection"
          tabID="introspection"
          isActive={() => currentTab === 'introspection'}
          onClick={() => setCurrentTab('introspection')}
        />
      </div>

      {firstPublish && (
        <FormControl className="mb-4" isInvalid={!!updateError}>
          <FormLabel>Federation version</FormLabel>
          <Select
            isDisabled={loading || updateLoading}
            className="w-full font-normal"
            value={data?.graph?.defaultBuildPipelineTrack ?? ''}
            onChange={handleBuildPipelineChange}
          >
            {Object.values(fedVersionOptions).map((version) => {
              const versionNumber = version
                .replace('FED_', '')
                .replace('_', '.');
              const label =
                version === BuildPipelineTrack.FED_NEXT
                  ? 'Federation Next (Experimental)'
                  : `Federation ${versionNumber}`;
              return (
                <option key={version} value={version}>
                  {label}
                </option>
              );
            })}
          </Select>
          {data?.graph?.defaultBuildPipelineTrack &&
            (data?.graph?.defaultBuildPipelineTrack ===
              BuildPipelineTrack.FED_NEXT ||
              compare(data?.graph?.defaultBuildPipelineTrack, '2.x', '=')) && (
              <FormHelperText iconType="info">
                Federation 2 requires <em>Router 0.9 or higher</em>. Older
                Router versions do not support Federation 2.
              </FormHelperText>
            )}
          <FormErrorMessage>
            There was a problem updating the build pipeline version. Please try
            again.
          </FormErrorMessage>
        </FormControl>
      )}

      <FederationGettingStartedInstructions firstPublish={firstPublish} />

      {currentTab === 'schemaDocument' && (
        <>
          <p className="pt-4">
            Use a local file containing the subgraph schema (e.g., called{' '}
            <OrbitCode>products-schema.graphql</OrbitCode>)
          </p>
          <Code
            className="mt-2"
            showCopyButton={true}
            firstLineDecorator="$ "
            highlight={fileSchemaScript.shellStringLinesToHighlight}
            mask={serviceApiToken.split(':').pop()}
            code={fileSchemaScript.shellString}
          />
        </>
      )}
      {currentTab === 'introspection' && (
        <>
          <p className="pt-4">
            Introspect a running subgraph (e.g., at{' '}
            <OrbitCode>http://localhost:4001</OrbitCode>)
          </p>
          <Code
            className="mt-2"
            showCopyButton={true}
            firstLineDecorator="$ "
            highlight={introspectSchemaScript.shellStringLinesToHighlight}
            mask={serviceApiToken.split(':').pop()}
            code={introspectSchemaScript.shellString}
          />
        </>
      )}
      <div className="mt-8 flex justify-between">
        {firstPublish && hasBuildErrors ? (
          <>
            <StatusBadge
              icon={<IconError className="size-4 text-icon-error" />}
            >
              Schema publish failed
            </StatusBadge>
            <ButtonGroup>
              {onClose && (
                <Button onClick={onClose} variant="secondary">
                  Close
                </Button>
              )}
              <Button
                as={ReactRouterLink}
                to={launchesRouteConfig.path({ graphId, graphVariant })}
                type="submit"
                variant="primary"
              >
                See build errors
              </Button>
            </ButtonGroup>
          </>
        ) : firstPublish ? (
          isPublished ? (
            <>
              <StatusBadge
                icon={<IconSuccess className="size-4 text-icon-success" />}
                colorScheme="green"
              >
                Schema published
              </StatusBadge>
              <ButtonGroup>
                <Button
                  as={ReactRouterLink}
                  to={launchesRouteConfig.path({ graphId, graphVariant })}
                  onClick={async () =>
                    ampli.seeSchemaClicked({
                      'API Type': 'GraphQL',
                      OnboardingVariant: 'GraphOS-Trial',
                    })
                  }
                  type="submit"
                  variant="primary"
                  className="ml-4 flex-none px-8"
                >
                  See schema changes
                </Button>
              </ButtonGroup>
            </>
          ) : (
            <>
              <StatusBadge icon={<Spinner size="tiny" />}>
                Awaiting schema publish
              </StatusBadge>
              <ButtonGroup>
                <Button
                  type="submit"
                  variant="primary"
                  onClick={() => {
                    if (firstPublish) {
                      ampli.finishLaterClicked({
                        'API Type': 'GraphQL',
                        OnboardingVariant: 'GraphOS-Trial',
                      });
                    }
                  }}
                >
                  {firstPublish ? 'Finish later' : 'Done'}
                </Button>
              </ButtonGroup>
            </>
          )
        ) : (
          <ButtonGroup className="ml-auto">
            <Button
              type="submit"
              variant="primary"
              onClick={async () => {
                return firstPublish
                  ? ampli.finishLaterClicked({
                      'API Type': 'GraphQL',
                      OnboardingVariant: 'GraphOS-Trial',
                    })
                  : ampli.onboardingCompleted({
                      'API Type': 'GraphQL',
                    });
              }}
            >
              {firstPublish ? 'Finish later' : 'Done'}
            </Button>
          </ButtonGroup>
        )}
      </div>
    </>
  );
};
